import React, { useCallback } from 'react';
import { FieldArrayRenderProps, useFieldArray } from 'react-final-form-arrays';

import { PurchaseFormValues } from '@occc-app/routes/Audience/components/Purchases/types';

import { useCanEditFormFieldsContext } from '@occc-app/App/CanEditFormFieldsContext';
import { InfoOutlinedIcon, Tooltip } from '@occc-app/design-system';
import { Stack } from '@mui/material';
import { audiencePurchasesInitialFormValues } from './constants';
import { PurchasesFormArrayItem } from './PurchasesFormArrayItem';

import { AddAnotherItem } from '../AddAnotherItem';
import { FormArrayItemSection } from '../FormArrayItemSection';
import { PRODUCTS_SECTION_INFO_TEXT } from '../messages';

interface IPurchasesFormContentProps {
  name: string;
}

export const PurchasesFormContent: React.FC<IPurchasesFormContentProps> = React.memo(({ name }) => {
  const { fields } = useFieldArray<PurchaseFormValues>(name, {
    subscription: {
      length: true,
    },
  });
  const handleAddItem = useCallback(
    () => fields.push(audiencePurchasesInitialFormValues as any),
    [fields],
  );
  const handleRemoveItem = useCallback((index: number) => fields.remove(index), [fields]);

  const { isEditable } = useCanEditFormFieldsContext();
  const isDeleteIconShown = isEditable && Boolean(fields.length && fields.length > 1);

  const renderContent: Parameters<
    FieldArrayRenderProps<PurchaseFormValues, any>['fields']['map']
  >[0] = useCallback(
    (fieldArrayName, index) => (
      <React.Fragment key={fieldArrayName}>
        <FormArrayItemSection
          index={index}
          title={
            <Stack direction='row' alignItems='center' component='span'>
              Products
              <Tooltip title={PRODUCTS_SECTION_INFO_TEXT} placement='top'>
                <InfoOutlinedIcon fontSize='small' color='action' sx={{ ml: 0.5 }} />
              </Tooltip>
            </Stack>
          }
          isDeleteIconShown={isDeleteIconShown}
          onDeleteClick={handleRemoveItem}
        />
        <PurchasesFormArrayItem key={fieldArrayName} fieldArrayName={fieldArrayName} />
      </React.Fragment>
    ),
    [handleRemoveItem, isDeleteIconShown],
  );

  return (
    <>
      {fields.map(renderContent)}
      <AddAnotherItem title='Add Another Transaction' onClick={handleAddItem} />
    </>
  );
});
